import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  CardContent,
  CardHeader,
  Avatar,
  Chip,
  LinearProgress,
  Alert,
  IconButton,
  Tooltip,
  Stack,
  CardActionArea,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Divider,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import {
  TrendingUp,
  TrendingDown,
  TrendingFlat,
  Refresh,
  OpenInNew,
  ShowChart,
  Shield,
  Newspaper,
  Timeline,
} from "@mui/icons-material";
import { marketDataApi } from "../services/marketDataApi";
import { recommendationsApi } from "../services/recommendationsApi";
import CommonDataGrid from "../components/grid/CommonDataGrid";
import SectionHeader from "../components/common/SectionHeader";
import AccentCard from "../components/common/AccentCard";

const isAsset = (a) =>
  a &&
  (a.assetType === "Crypto" ||
    a.assetType === 0 ||
    a.assetType === "Stock" ||
    a.assetType === 1);

const isCrypto = (a) => a?.assetType === "Crypto" || a?.assetType === 0;

const sortModes = [
  { value: "recommended", label: "Top Recommended" },
  { value: "volume", label: "Top Volume" },
  { value: "marketCap", label: "Top Market Cap" },
  { value: "priceChange", label: "Top Price Change (24h)" },
  { value: "volatility", label: "Top Volatility" },
];

const recommendationRank = {
  StrongBuy: 5,
  Buy: 4,
  Hold: 3,
  Sell: 2,
  StrongSell: 1,
};

const DashboardPage = () => {
  const navigate = useNavigate();
  const [topMode, setTopMode] = React.useState("recommended");

  const {
    data: marketAssets = [],
    isLoading: assetsLoading,
    error: assetsError,
  } = marketDataApi.endpoints.getAllMarketAssets.useQuery();

  const {
    data: recommendationsData = [],
    isLoading: recommendationsLoading,
    error: recommendationsError,
  } = recommendationsApi.endpoints.getAllLatestRecommendations.useQuery();

  const [refreshMarketData] =
    marketDataApi.endpoints.refreshMarketData.useMutation();

  const assets = React.useMemo(
    () => marketAssets.filter(isAsset),
    [marketAssets]
  );

  const recommendationsMap = React.useMemo(() => {
    return recommendationsData.reduce((acc, rec) => {
      acc[rec.marketAssetId] = rec;
      return acc;
    }, {});
  }, [recommendationsData]);

  const handleRefreshAsset = async (e, marketAssetId) => {
    e.stopPropagation();
    try {
      await refreshMarketData(marketAssetId).unwrap();
    } catch (error) {
      console.error("Failed to refresh market data:", error);
    }
  };

  const formatCurrency = (value) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 8,
    }).format(value || 0);

  const formatCompactMoney = (value) => {
    if (value == null) return "—";
    if (value >= 1e12) return `$${(value / 1e12).toFixed(2)}T`;
    if (value >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
    if (value >= 1e6) return `$${(value / 1e6).toFixed(2)}M`;
    if (value >= 1e3) return `$${(value / 1e3).toFixed(2)}K`;
    return formatCurrency(value);
  };

  const getPriceChangeIcon = (change) => {
    if (change > 0) return <TrendingUp color="success" fontSize="small" />;
    if (change < 0) return <TrendingDown color="error" fontSize="small" />;
    return <TrendingFlat color="disabled" fontSize="small" />;
  };

  const getTopScore = (asset) => {
    const rec = recommendationsMap[asset.marketAssetId];

    switch (topMode) {
      case "volume":
        return Number(asset.totalVolume || 0);
      case "marketCap":
        return Number(asset.marketCap || 0);
      case "priceChange":
        return Number(asset.priceChangePercentage24h || 0);
      case "volatility":
        return Number(rec?.volatilityAnalysis?.volatilityScore || 0);
      case "recommended":
      default: {
        const level = rec?.overallRecommendation?.recommendationLevel;
        const score = recommendationRank[level] || 0;
        const aiScore = Number(rec?.overallRecommendation?.score || 0) / 100;
        return score + aiScore;
      }
    }
  };

  const topAssets = React.useMemo(() => {
    return [...assets]
      .sort((a, b) => getTopScore(b) - getTopScore(a))
      .slice(0, 4);
  }, [assets, topMode, recommendationsMap]);

  const tableRows = React.useMemo(() => {
    return assets.map((asset) => {
      const rec = recommendationsMap[asset.marketAssetId];
      return {
        ...asset,
        outlook: rec?.overallRecommendation?.recommendationLevel || "—",
        trend: rec?.trendAnalysis?.trend || "—",
        risk: rec?.riskAssessment?.level || "—",
        sentiment: rec?.sentimentAnalysis?.level || "—",
        volatility: rec?.volatilityAnalysis?.level || "—",
        aiDate: rec?.dateGenerated || null,
      };
    });
  }, [assets, recommendationsMap]);

  const columns = React.useMemo(
    () => [
      {
        field: "asset",
        headerName: "Asset",
        sortable: false,
        renderCell: (row) => (
          <Stack direction="row" spacing={1} alignItems="center">
            <Avatar src={row.imageUrl} alt={row.symbol} sx={{ width: 28, height: 28 }}>
              {row.symbol?.charAt(0)}
            </Avatar>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                {row.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {row.symbol}
              </Typography>
            </Box>
          </Stack>
        ),
      },
      {
        field: "assetType",
        headerName: "Type",
        valueGetter: (row) => (isCrypto(row) ? "Crypto" : "Stock"),
        renderCell: (row) => (
          <Chip
            size="small"
            color={isCrypto(row) ? "secondary" : "primary"}
            label={isCrypto(row) ? "Crypto" : "Stock"}
          />
        ),
      },
      {
        field: "exchangeSymbol",
        headerName: "Exchange",
        valueGetter: (row) => row.exchangeSymbol || "—",
      },
      {
        field: "currentPrice",
        headerName: "Price",
        align: "right",
        valueGetter: (row) => Number(row.currentPrice || 0),
        valueFormatter: (value) => formatCurrency(value),
      },
      {
        field: "priceChangePercentage24h",
        headerName: "24h %",
        align: "right",
        valueGetter: (row) => Number(row.priceChangePercentage24h || 0),
        renderCell: (row) => (
          <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
            {getPriceChangeIcon(row.priceChangePercentage24h)}
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color:
                  row.priceChangePercentage24h > 0
                    ? "success.main"
                    : row.priceChangePercentage24h < 0
                    ? "error.main"
                    : "text.secondary",
              }}
            >
              {row.priceChangePercentage24h != null
                ? `${Number(row.priceChangePercentage24h).toFixed(2)}%`
                : "—"}
            </Typography>
          </Stack>
        ),
      },
      {
        field: "marketCap",
        headerName: "Market Cap",
        align: "right",
        valueGetter: (row) => Number(row.marketCap || 0),
        valueFormatter: (value) => formatCompactMoney(value),
      },
      {
        field: "totalVolume",
        headerName: "Volume",
        align: "right",
        valueGetter: (row) => Number(row.totalVolume || 0),
        valueFormatter: (value) => formatCompactMoney(value),
      },
      { field: "outlook", headerName: "Outlook", valueGetter: (row) => row.outlook },
      { field: "trend", headerName: "Trend", valueGetter: (row) => row.trend },
      { field: "risk", headerName: "Risk", valueGetter: (row) => row.risk },
      { field: "sentiment", headerName: "Sentiment", valueGetter: (row) => row.sentiment },
      { field: "volatility", headerName: "Volatility", valueGetter: (row) => row.volatility },
      {
        field: "actions",
        headerName: "",
        sortable: false,
        align: "right",
        renderCell: (row) => (
          <Tooltip title="Refresh">
            <IconButton
              size="small"
              onClick={(e) => handleRefreshAsset(e, row.marketAssetId)}
              aria-label="Refresh"
            >
              <Refresh fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    [recommendationsMap]
  );

  if (assetsLoading || recommendationsLoading) {
    return (
      <Box sx={{ width: "100%", maxWidth: 1200, mx: "auto", mt: 2 }}>
        <LinearProgress />
        <Typography variant="body2" sx={{ mt: 2, textAlign: "center" }}>
          Loading dashboard...
        </Typography>
      </Box>
    );
  }

  if (assetsError || recommendationsError) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Failed to load dashboard data. Please try again.
      </Alert>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        minWidth: 0,
      }}
    >
      <Box sx={{ mb: 3 }}>
        <SectionHeader variant="h4" component="h1" gutterBottom sx={{ pb: 0.75 }}>
          Dashboard
        </SectionHeader>
        <Typography variant="body1" color="text.secondary">
          At-a-glance rankings plus a sortable, filterable asset table.
        </Typography>
      </Box>

      <AccentCard
        sx={{
          p: 2,
          borderRadius: 2,
          mb: 2,
          minWidth: 0,
          maxWidth: "100%",
          overflow: "hidden",
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "center" }}
          spacing={2}
          sx={{ mb: 2, minWidth: 0 }}
        >
          <SectionHeader variant="h6" sx={{ minWidth: 0 }}>
            Top Assets
          </SectionHeader>
          <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 260 }, maxWidth: "100%" }}>
            <InputLabel id="top-assets-mode-label">Show</InputLabel>
            <Select
              labelId="top-assets-mode-label"
              value={topMode}
              label="Show"
              onChange={(e) => setTopMode(e.target.value)}
            >
              {sortModes.map((m) => (
                <MenuItem key={m.value} value={m.value}>
                  {m.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        <Grid container spacing={2} sx={{ width: "100%", minWidth: 0 }}>
          {topAssets.map((asset) => {
            const rec = recommendationsMap[asset.marketAssetId];
            return (
              <Grid key={asset.marketAssetId} size={{ xs: 12, sm: 6, lg: 3 }} sx={{ minWidth: 0, display: "flex" }}>
                <AccentCard
                  variant="outlined"
                  sx={{
                    borderRadius: 2,
                    width: "100%",
                    minWidth: 0,
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                  }}
                >
                <CardActionArea onClick={() => navigate(`/dashboard/asset/${asset.marketAssetId}`)} sx={{ flex: 1, alignItems: "stretch" }}>
                  <CardHeader
                    sx={{ pb: 1, "& .MuiCardHeader-content": { minWidth: 0 } }}
                    avatar={
                      <Avatar src={asset.imageUrl} alt={asset.symbol} sx={{ width: 36, height: 36, flexShrink: 0 }}>
                        {asset.symbol?.charAt(0)}
                      </Avatar>
                    }
                    title={asset.symbol}
                    subheader={asset.name}
                    titleTypographyProps={{ noWrap: true, variant: "subtitle1", sx: { fontWeight: 700 } }}
                    subheaderTypographyProps={{ noWrap: true, title: asset.name }}
                    action={
                      <IconButton
                        size="small"
                        aria-label="Open detail"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/dashboard/asset/${asset.marketAssetId}`);
                        }}
                      >
                        <OpenInNew fontSize="small" />
                      </IconButton>
                    }
                  />
                  <CardContent sx={{ pt: 0, minWidth: 0 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }} noWrap>
                      {formatCurrency(asset.currentPrice)}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ my: 1, minWidth: 0, flexWrap: "wrap" }}>
                      {getPriceChangeIcon(asset.priceChangePercentage24h)}
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {asset.priceChangePercentage24h != null
                          ? `${Number(asset.priceChangePercentage24h).toFixed(2)}%`
                          : "—"}
                      </Typography>
                      <Chip
                        size="small"
                        color={isCrypto(asset) ? "secondary" : "primary"}
                        label={isCrypto(asset) ? "Crypto" : "Stock"}
                      />
                    </Stack>
                    <Divider sx={{ my: 1 }} />
                    <Stack
                      direction="row"
                      spacing={1}
                      flexWrap="wrap"
                      useFlexGap
                      sx={{ minWidth: 0, "& .MuiChip-root": { maxWidth: "100%" } }}
                    >
                      <Chip size="small" variant="outlined" label={`MCap ${formatCompactMoney(asset.marketCap)}`} />
                      <Chip size="small" variant="outlined" label={`Vol ${formatCompactMoney(asset.totalVolume)}`} />
                      <Chip size="small" variant="outlined" icon={<ShowChart sx={{ fontSize: 16 }} />} label={rec?.overallRecommendation?.recommendationLevel || "No Outlook"} />
                      <Chip size="small" variant="outlined" icon={<Shield sx={{ fontSize: 16 }} />} label={rec?.riskAssessment?.level || "No Risk"} />
                      <Chip size="small" variant="outlined" icon={<Newspaper sx={{ fontSize: 16 }} />} label={rec?.sentimentAnalysis?.level || "No Sentiment"} />
                      <Chip size="small" variant="outlined" icon={<Timeline sx={{ fontSize: 16 }} />} label={rec?.volatilityAnalysis?.level || "No Vol"} />
                    </Stack>
                  </CardContent>
                </CardActionArea>
              </AccentCard>
              </Grid>
            );
          })}
        </Grid>
      </AccentCard>

      <CommonDataGrid
        rows={tableRows}
        columns={columns}
        getRowId={(row) => row.marketAssetId}
        onRowClick={(row) => navigate(`/dashboard/asset/${row.marketAssetId}`)}
        initialSortField="marketCap"
        initialSortDirection="desc"
        filterPlaceholder="Filter assets by symbol, name, exchange, outlook, trend, risk..."
      />
    </Box>
  );
};

export default DashboardPage;